import * as React from 'react';
import styled from '@emotion/styled';
import { useStaticQuery, graphql } from 'gatsby';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { GatsbySite } from '../../graphql';
import SEO from '../../components/seo';
import { CenteredContent } from '../../components/utility/section';
import ResponsiveDiv from '../../components/utility/ResponsiveDiv';
import { SpacingWrapper } from '../../components/utility/spacing-wrapper';
import TitlePage from '../../components/project/TitlePage';
import Client from '../../components/project/client';
import Section from '../../components/project/section';
import Quote from '../../components/project/quote';
import ResponsiveParagraph from '../../components/utility/ResponsiveParagraph';
import { SectionTag } from '../../components/project/section-tag';
import Tech from '../../components/project/tech';
import { HideOnMobile } from '../../components/utility/hide-on';
import Video from '../../components/video';
import NextMissions from '../../components/project/next-missions';
// import images
import background from '../../images/projects/enjoyn/background.png';
import heroVideo from '../../images/projects/steelhaus/hero-video.mp4';
import reactLogo from '../../images/projects/otc/react-logo.png';
import schedule from '../../images/projects/steelhaus/schedule.png';
import jobInProgress from '../../images/projects/steelhaus/job-in-progress.png';
import desktop from '../../images/projects/steelhaus/desktop.png';
import logo from '../../images/projects/steelhaus/logo.png';
import twoTablets from '../../images/projects/steelhaus/two-tablets.png';
import robbieButchart from '../../images/projects/employee-avatars/robbie.png';
import threeTablets from '../../images/projects/steelhaus/three-tablets.png';

const SteelhausPage = () => {
  const { site } = useStaticQuery<GatsbySite>(
    graphql`
      query {
        site {
          siteMetadata {
            projects {
              name
              description
              imageName
              link
            }
          }
        }
      }
    `,
  );

  const { siteMetadata } = site;

  const projects = siteMetadata?.projects || [];
  return (
    <>
      <SEO title="Steelhaus" />
      <div id="#top" />
      <TitlePage
        title="Steelhaus: Scheduling, optimization and real-time visualization"
        content="Hardware to talk to the machines, software to make sense of the mumbo-jumbo and clean interface for everyone to use. Add operational efficiency and planning through real-time data into the mix. The word is harmony."
        backgroundImage={background}
      />
      <SpacingWrapper>
        <Client
          content={`Steelhaus is an industrial tools manufacturer of innovative and quality steel products for the energy industry. Started in 2008, the Calgary-based company has grown to multiple manufacturing plants in Western Canada, multiple field and sales offices, and partnerships with companies in more than 20 countries.`}
          images={[{ image: logo, url: 'https://steelhausinc.com/' }]}
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <HideOnMobile breakpoint={1024}>
          <Video title="A view of the menu" src={heroVideo} />
        </HideOnMobile>
      </SpacingWrapper>

      <SpacingWrapper>
        <Section
          p1={`Operational efficiency is a key objective of any manufacturing company, especially one in the growth phase. Metrics like throughput and cycle time become important data points for any sort of forecasting and planning. 
          Throw in Lean principles, and decision-makers become obsessed with downtimes and capacity utilization.  
          Steelhaus has seen tremendous growth and has had to target optimization of its plant operations.  `}
          p2={`Integration with the company’s existing fleet of manufacturing machines was key, and a touchscreen-enabled hardware was built to pull data. Next up was building an intelligent software platform that featured AI-driven scheduling, equipment optimization and real-time analytics. 
          The platform gave a quick rundown of ongoing, completed and scheduled activities for each plant, besides helping operations control keep track of downtimes and maintenance. 
          The simplicity, the efficiency, the harmony of it all working together, very Apple-sque, no? `}
          heading="House of steel"
          subHeading="summary"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img src={twoTablets} alt="A tablet view of the dashboard" style={{ width: '100%' }} />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>

      <SpacingWrapper>
        <Section
          p1={`For any manufacturer, capital expenditure to set up the plant can be exorbitant. It also means that expansion plans require a lot more resources, planning and informed decisions. It doesn't help that unlike computers and desktops, software updates to match new requirements and desired functionalities are not an option with lathes and mills. 
          With multiple lathes and mills forming the shop floor at multiple locations, Steelhaus needed visibility on manufacturing processes to facilitate planning and strategy in the long term. 
          Their existing fleet of machinery did not have the capability to facilitate data-driven decisions in their current forms. `}
          p2={`Consequently, real-time analytics, optimized manufacturing and scheduling, data points that would help in maximizing profits were non-existent. Without information about existing capacities, driving expansion strategies can be challenging to say the least. 
          After multiple attempts by different entities at creating a solution that does it all, the challenge was met by Launchcode and our awesome developers. 
          Did we mention bragging rights and a dinner bet was on the table as part of the negotiations too? `}
          heading="Seeing through the haze"
          subHeading="pain"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <Quote
          quote="It's about quantifying your scalability metrics. If you can scale the same hardware, optimize it, and create other efficiencies, your utilization goes up. Transparency and real-time analytics that lead to better strategy and planning is the need of the hour."
          citation="Robbie Butchart"
          citation2="CRO @ Launchcode"
          picture={robbieButchart}
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={threeTablets}
              alt="Three tablets showing different screens"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>
      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <SectionTag>vision</SectionTag>
            <h2 style={{ fontWeight: 500, fontSize: 48, maxWidth: 730, textAlign: 'left' }}>
              <strong>Optimize, optimize, optimize</strong>
            </h2>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              Steelhaus needed a solution that could extract information from their existing
              machinery, integrate with a recent enterprise resource planning (ERP) tool they had
              onboarded, create an interface that could allow for real-time tracking of KPIs,
              facilitate scheduling and optimize capacities.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>
      <SpacingWrapper>
        <Tech
          content={`React allows for solutions to be responsive, which means they can be run on different screen sizes and platforms without having to nurture the code and the development process.
          From the touchscreen-enabled hardware that connected to the existing fleet of lathes and mills, to the management’s mobile screen, the stack would allow for real-time monitoring in the actual sense of the time. `}
          images={[reactLogo]}
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={schedule}
              alt="Web view of the scheduling platform"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>
      <SpacingWrapper>
        <Section
          p1={`It all started all those years ago (read early 2019) when Steelhaus faced challenges with their ERP implementation. Conversations through the Dream meeting (with the client and key personnel) and the (internal) Thinktank sessions at Launchcode gave rise to a scheduling and monitoring platform that would go above and beyond. 
          Understanding processes and functions of the business is key to Launchcode's process.  
          Thinktank sessions considered the possibility of creating a hardware solution that would connect to the existing machinery and try and make sense of its work status. While some of the newer generation equipment were willing, the older generation machines, not so much.`}
          p2={`The process involved connecting with machine manufacturers for any firmware details, and then building an interface that could make it simpler for machinists to input the work status. 
          The work and machine status data that emerged out of this was invaluable to Steelhaus for streamlining processes. 
          The next step was a visualization tool that helped management make sense of downtimes and lack of efficiencies, if any.     
          Launchcode developers even created a kanban style scheduling board that would make it simpler for shop floor managers to schedule and re-schedule any jobs depending on machine, material and personnel availability. `}
          heading="In the quest of a tracking & optimizing solution"
          subHeading="journey"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <MultipleDeviceImageWrapper>
              <img src={jobInProgress} alt="A tablet displaying the 'job in progress' screen" />
            </MultipleDeviceImageWrapper>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <SectionTag>ux/ui</SectionTag>
            <h2 style={{ fontWeight: 500, fontSize: 48, maxWidth: 730, textAlign: 'left' }}>
              <strong>What you see is what you get</strong>
            </h2>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              The touchscreen unit connected to the machines had a simple interface that used color
              coding to grab attention in case of downtimes and gave machinists simple choices to
              input reasons for downtimes. It even had a timer for downtimes and scheduled
              completion of jobs among other things.
              <br />
              <br />
              The kanban style scheduling board, backed with AI, made it simpler for managers to
              schedule jobs on appropriate machines, plan for materials and supplies, and
              maintenance.
              <br />
              <br />
              The responsive nature of the solution meant that keeping a track of productivity and
              efficiency was just a click away on a smartphone browser.
              <br />
              <br />
              The solution even had a dashboard solution that was deployed on multiple TV screens
              around the shop floor and administration offices for round-the-clock tracking. It also
              meant transparency among machinists and administration about status of work and
              personnel.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>
      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={desktop}
              alt="A desktop computer showcasing the app"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>
      <SpacingWrapper>
        <Section
          p1={`After months of development emerged the Steelhaus Monitoring Platform. 
          The hardware aspect of the solution connected seamlessly with their machines, and was able to display operational information on the screen for personnel to take appropriate actions during crucial events - refilling raw material, need for maintenance, operational status, etc. `}
          p2={`Some of the first tranches of data itself provided Steelhaus with a couple of efficiency opportunities that would eventually reduce machine downtime, chief among them was to speed up material supplies for respective lathes/mills using a modern conveyor belt supply system. 
          The kanban style scheduling dashboard allowed floor managers to assign tasks to machines, predict material and supply shortages, optimize use of machinery, make personnel schedules, among other things. `}
          heading="Visibility made easy"
          subHeading="results"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <Section
          p1={`A dashboard solution was also deployed on multiple TV screens around the shop floor, and it made the personnel scheduling and assigning work a lot more transparent. A quick look at the screen and the viewer knew the jobs that were underway at any of the lathes/mills and the personnel assigned to oversee the job.`}
          p2={`The touchscreen-enabled hardware provided status updates on personnel breaks, machine status and job status. 
          The responsive nature of the solution meant that managers away from the floors, or in some cases, even off the site, could keep tabs and give real-time updates to stakeholders. `}
          heading="Tools that work"
          subHeading="response"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <Quote
          quote="They were energetic. When they said they were going to do something, they did it. We gave them some tough feedback, and they took everything in stride."
          citation="Stephen Truzak"
          citation2="General Manager @ Steelhaus"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <ResponsiveDiv>
          <CitationsTitleWrapper>
            <h2
              style={{
                fontWeight: 500,
                fontSize: 48,
                maxWidth: 730,
                textAlign: 'left',
              }}
            >
              <strong>Citations</strong>
            </h2>
          </CitationsTitleWrapper>
        </ResponsiveDiv>

        <ResponsiveDiv>
          <ResponsiveParagraph>
            <p>
              <sup>1</sup>
              <a
                href="https://awcbc.org/en/statistics/national-work-injury-disease-and-fatality-statistics-nwisp-year-at-a-glance/"
                target="_blank"
                rel="nofollow noreferrer"
              >
                “National Work Injury, Disease and Fatality Statistics (NWISP) - Year At A Glance”.
              </a>{' '}
              Association of Workers' Compensation Boards of Canada. Accessed 24 May 2022.{' '}
            </p>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p>
              <sup>2</sup>
              <a
                href="https://www.thesafetymag.com/ca/topics/safety-and-ppe/the-five-most-common-types-of-workplace-injuries/316462"
                target="_blank"
                rel="nofollow noreferrer"
              >
                Foulis, Maia. “The five most common types of workplace injuries”.
              </a>{' '}
              The Safety Mag, 15 November 2021. Accessed 1 June 2022.{' '}
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>

      <NextMissions currentProjectName="Steelhaus" projects={projects} />

      <CenteredContent style={{ marginBottom: 40 }}>
        <a href="#top">Back to top ⇮</a>
      </CenteredContent>
    </>
  );
};

const SimpleUXImageWrapper = styled.div`
  > div {
    overflow: unset;
    max-width: 70ch;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    img {
      top: 12.5%;
    }
  }
`;

const CitationsTitleWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.xs}) {
    margin: 0px 0px 0px 0px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin: 0px 0px 0px 0px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-left: 40px;
  }

  @media screen and (min-width: 1250px) {
    margin-left: 40px;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.wideScreen}) {
    margin-left: 40px;
  }
`;

const MultipleDeviceImageWrapper = styled.div`
  width: 100%;
  height: 100%;

  > div {
    transform: scale(1.075);
  }
`;

export default SteelhausPage;
